/* eslint-disable camelcase */
import _ from 'lodash';
import {
	mapCartItems,
	mapCartCoupons,
	mapShippingOptions,
	getPriceBreakdown,
	validateAddress,
	validateEmail,
	CREDIT_CARD,
	PAYPAL,
	USA_CANADA_COUNTRY_CODES,
	addressesHaveSameTaxLocation,
} from '@js/service/checkoutService';

export default {
	pageReady(state) {
		return !!state.cart && state.initialized;
	},

	modifyingOrSubmittingOrder(state) {
		return state.modifyingCart || state.submittingOrder;
	},

	items(state) {
		return mapCartItems(state.cart.order_offers);
	},

	cartProducts(state) {
		return state.cart.order_offers.reduce((carry, orderOffer) => {
			if (orderOffer.type !== 'product') return carry;

			return [...carry, ...orderOffer.offer.products];
		}, []);
	},

	blacklistedProducts(state, getters) {
		if (getters.isDigitalProduct) return [];
		if (!getters.shippingCountry) return [];

		return getters.cartProducts
			.filter((product) =>
				product.country_blacklist.includes(getters.shippingCountry)
			)
			.map(({
				name,
				sku
			}) => ({
				name,
				sku
			}));
	},

	itemForOffer(state, getters) {
		return (offerId) => _.find(getters.items, { offerId });
	},

	cartShippingAddress(state) {
		return _.get(state, 'cart.shipping_address');
	},

	cartBillingAddress(state) {
		return _.get(state, 'cart.billing_address');
	},

	coupons(state) {
		return mapCartCoupons(state.cart.order_offers);
	},

	hasBumpers(state) {
		return state.bumpers.length > 0;
	},

	shippingOptions(state) {
		return mapShippingOptions(
			_.get(state, 'cart.available_shipping_categories', [])
		);
	},

	isDigitalProduct(state, getters) {
		return getters.shippingOptions.length === 0;
	},

	isPhysicalProduct(state, getters) {
		return !getters.isDigitalProduct;
	},

	isInternational(state, getters) {
		return (
			getters.isPhysicalProduct && state.shippingAddress.country !== 'USA'
		);
	},

	isInvalidShipping(state, getters) {
		if (getters.isDigitalProduct) return false;
		if (state.shippingDifferentFromBilling) return false;
		return !getters.billingAddressIsUsaOrCanada;
	},

	hasEmailError(state) {
		return !validateEmail(state.email);
	},

	hasUserOrNoEmailErrors(state, getters) {
		return !!state.user || !getters.hasEmailError;
	},

	emailExistsErrorOrChecking(state) {
		return state.emailExistsError || state.checkingEmailExists;
	},

	contactVerified(state, getters) {
		return (
			getters.hasUserOrNoEmailErrors &&
			!state.emailExistsErrorOrChecking
		);
	},

	productLimits(state) {
		return state.productLimits;
	},

	billingAddressErrors(state) {
		const fields = validateAddress(state.billingAddress);

		if (!state.billingAddress.phone_number) {
			fields.push('phone_number');
		}

		return fields;
	},

	hasBillingAddressErrors(state, getters) {
		return getters.billingAddressErrors.length > 0;
	},

	billingAddressVerified(state, getters) {
		return (
			!getters.hasBillingAddressErrors &&
			!state.billingPostalCodeMismatchError
		);
	},

	billingAddressIsUsaOrCanada(state) {
		return USA_CANADA_COUNTRY_CODES.includes(
			_.get(state, 'billingAddress.country')
		);
	},

	shippingAddressErrors(state) {
		const fields = validateAddress(state.shippingAddress);

		if (!state.shippingAddress.phone_number) {
			fields.push('phone_number');
		}

		return fields;
	},

	hasShippingAddressErrors(state, getters) {
		return getters.shippingAddressErrors.length > 0;
	},

	shippingAddressVerified(state, getters) {
		return (
			!getters.hasShippingAddressErrors &&
			!state.shippingPostalCodeMismatchError
		);
	},

	contactInformationVisible(state) {
		return state.isEcommerce && !state.user;
	},

	shippingAddressToggleVisible(state, getters) {
		return getters.isPhysicalProduct || getters.billingAddressIsUsaOrCanada;
	},

	shippingAddressVisible(state, getters) {
		return state.shippingDifferentFromBilling && getters.shippingAddressToggleVisible;
	},

	checkoutButtonVisible(state, getters) {
		return getters.blacklistedProducts.length === 0 && !getters.isInvalidShipping;
	},

	shippingCountry(state, getters) {
		return _.get(getters, 'addressToShip.country');
	},

	addressToShip(state) {
		return state.shippingDifferentFromBilling
			? state.shippingAddress
			: state.billingAddress;
	},

	taxAndShippingAddress(state, getters) {
		if (getters.isDigitalProduct) return state.billingAddress;

		return state.shippingDifferentFromBilling ? state.shippingAddress : state.billingAddress;
	},

	taxAndShippingAddressHasErrors(state, getters) {
		if (getters.isDigitalProduct) return getters.hasBillingAddressErrors;

		return state.shippingDifferentFromBilling ? getters.hasShippingAddressErrors : getters.hasBillingAddressErrors;
	},

	taxAndShippingAddressSynced(state, getters) {
		return getters.cartTaxAndShippingAddress && addressesHaveSameTaxLocation(getters.cartTaxAndShippingAddress, getters.taxAndShippingAddress);
	},

	cartTaxAndShippingAddress(state, getters) {
		return getters.isDigitalProduct ? getters.cartBillingAddress : getters.cartShippingAddress;
	},

	formHasErrors(state, getters) {
		if (!getters.contactVerified) return true;

		return (
			(state.shippingDifferentFromBilling &&
				!getters.shippingAddressVerified) ||
			!getters.billingAddressVerified
		);
	},

	priceBreakdown(state, getters) {
		return getPriceBreakdown(
			state.cart,
			getters.isDigitalProduct
				? !getters.hasBillingAddressErrors
				: getters.shippingAddressSynced
		);
	},

	shippingAddressSynced(state, getters) {
		if (!state.cart.shipping_address) return false;

		const addressEqual = (address) => state.cart.shipping_address.postal_code ===
			address.postal_code &&
			state.cart.shipping_address.country ===
			address.country;

		if (state.shippingDifferentFromBilling) {
			return !getters.hasShippingAddressErrors && addressEqual(state.shippingAddress);
		}

		return !getters.hasBillingAddressErrors && addressEqual(state.billingAddress);
	},

	paypalOrderOptions(state) {
		return {
			requestBillingAgreement: true,
			flow: 'checkout',
			amount: state.cart.amount_base.decimal,
			currency: 'USD',
			enableShippingAddress: false,
			shippingAddressEditable: false,
		};
	},

	payingWithCreditCard(state) {
		return state.paymentProcessor === CREDIT_CARD;
	},

	enablePaypal(state) {
		return state.paymentProcessors.includes(PAYPAL);
	},

	payingWithPaypal(state) {
		return state.paymentProcessor === PAYPAL;
	},
};
